/**
 * UpdateStatusModal.tsx - Fundraiser Status Management Modal
 *
 * This component provides an interface for updating fundraiser status
 * with appropriate warnings, validations, and impact assessments.
 *
 * Key Features:
 * - Status change interface with validation
 * - Impact assessment for status changes
 * - Warning messages for critical changes
 * - Date validation for status transitions
 * - Confirmation requirements for destructive changes
 * - Status history and audit trail
 *
 * Status Options:
 * - Active: Fundraiser accepting donations
 * - Paused: Temporarily stopped, can be resumed
 * - Completed: Finished successfully
 * - Inactive: Disabled, not accepting donations
 * - Draft: Not yet published
 *
 * Props:
 * - isVisible: boolean - Modal visibility state
 * - onClose: () => void - Close modal handler
 * - fundraiser: Fundraiser - Fundraiser to update status for
 * - onStatusUpdate: (status: string) => void - Status update handler
 *
 * Dependencies: Fundraiser types, status validation, portal rendering
 */

import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { X, ChevronRight, ChevronDown } from "lucide-react";
import { FundRaiserStatus } from "../enums";
import { Fundraiser } from "../models/FundraisersGridModels";
import { firstValueFrom } from "rxjs";
import { FundraiserService } from "../services";
import { AuthService } from "@mindmemobile/mm-shared-global";
import { fundraiserStore } from "../stores";
import { CreateFundraiserModel } from "../models";
import ToastService from "./ToastService";
import { canManageFundraiser } from "../utils/permissions";

const ModalOverlay = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: ${(props) => (props.visible ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  background: #ffffff;
  border-radius: 24px;
  width: 100%;
  max-width: 550px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.1);
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    background: rgba(0, 0, 0, 0.2);
    color: #1e293b;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ModalHeader = styled.div`
  text-align: center;
  padding: 40px 40px 20px;
  border-bottom: 1px solid #f1f5f9;
`;

const ModalTitle = styled.h2`
  font-size: 28px;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
  line-height: 1.1;
`;

const ContentContainer = styled.div`
  padding: 32px 40px;
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 24px;
  justify-content: center;
`;

const StatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const StatusLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CurrentStatus = styled.div<{ status: string }>`
  display: inline-block;
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${(props) => {
    switch (props.status) {
      case "Active":
        return "background: #dcfce7; color: #166534; border: 2px solid #16a34a;";
      case "Completed":
        return "background: #dbeafe; color: #1e40af; border: 2px solid #2563eb;";
      case "Inactive":
        return "background: #fef3c7; color: #92400e; border: 2px solid #ca8a04;";
      case "Draft":
        return "background: #e5e7eb; color: #374151; border: 2px solid #9ca3af;";
      default:
        return "background: #6b7280; color: #ffffff; border: 2px solid #374151;";
    }
  }}
`;

const ArrowIcon = styled.div`
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 26px;
`;

const DropdownContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const DropdownButton = styled.button<{
  hasSelection: boolean;
  selectedStatus?: string;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 24px;
  border: 2px solid
    ${(props) => {
      if (!props.hasSelection) return "#e2e8f0";
      switch (props.selectedStatus) {
        case "Active":
          return "#16a34a";
        case "Completed":
          return "#2563eb";
        case "Inactive":
          return "#ca8a04";
        case "Draft":
          return "#9ca3af";
        default:
          return "#e2e8f0";
      }
    }};
  border-radius: 12px;
  background: ${(props) => {
    if (!props.hasSelection) return "#ffffff";
    switch (props.selectedStatus) {
      case "Active":
        return "#dcfce7";
      case "Completed":
        return "#dbeafe";
      case "Inactive":
        return "#fef3c7";
      case "Draft":
        return "#e5e7eb";
      default:
        return "#ffffff";
    }
  }};
  color: ${(props) => {
    if (!props.hasSelection) return "#dbeafe";
    switch (props.selectedStatus) {
      case "Active":
        return "#166534";
      case "Completed":
        return "#1e40af";
      case "Inactive":
        return "#92400e";
      case "Draft":
        return "#374151";
      default:
        return "#1e293b";
    }
  }};
  color: ${(props) => {
    if (!props.hasSelection) return "#1e293b";
    switch (props.selectedStatus) {
      case "  Active":
        return "#166534";
      case "Completed":
        return "#1e40af";
      case "Inactive":
        return "#92400e";
      case "Draft":
        return "#374151";
      default:
        return "#1e293b";
    }
  }};
  font-size: 16px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 220px;

  &:hover {
    border-color: #3b82f6;
    background: #f8fafc;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const DropdownMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  background: #ffffff;
  border: 2px solid #e2e8f0;
  border-top: none;
  border-radius: 0 0 12px 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  opacity: ${(props) => (props.isOpen ? 1 : 0)};
  visibility: ${(props) => (props.isOpen ? "visible" : "hidden")};
  transform: ${(props) =>
    props.isOpen ? "translateY(0)" : "translateY(-4px)"};
  transition: all 0.2s ease;
  margin-top: -2px;
`;

const DropdownItem = styled.button<{ status: string }>`
  width: 100%;
  padding: 12px 24px;
  border: none;
  background: #ffffff;
  color: #1e293b;
  font-size: 16px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover {
    background: ${(props) => {
      switch (props.status) {
        case "Active":
          return "#dcfce7";
        case "Completed":
          return "#dbeafe";
        case "Inactive":
          return "#fef3c7";
        case "Draft":
          return "#e5e7eb";
        default:
          return "#f8fafc";
      }
    }};
    color: ${(props) => {
      switch (props.status) {
        case "Active":
          return "#166534";
        case "Completed":
          return "#1e40af";
        case "Inactive":
          return "#92400e";
        case "Draft":
          return "#374151";
        default:
          return "#1e293b";
      }
    }};
  }

  &:not(:last-child) {
    border-bottom: 1px solid #f1f5f9;
  }
`;

const MessageContainer = styled.div`
  padding: 16px;
  margin-bottom: 24px;
  min-height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const StatusMessage = styled.div`
  font-size: 16px;
  color: #374151;
  font-weight: 500;
  line-height: 1.4;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  padding: 16px 40px 24px;
  border-top: 1px solid #f1f5f9;
`;

const ModalButton = styled.button<{ variant?: "primary" }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  ${(props) =>
    props.variant === "primary"
      ? `
    background: #3b82f6;
    color: #ffffff;

    &:hover {
      background: #2563eb;
    }

    &:disabled {
      background: #9ca3af;
      cursor: not-allowed;
    }
  `
      : `
    background: #f1f5f9;
    color: #64748b;

    &:hover {
      background: #e2e8f0;
    }
  `}
`;

interface UpdateStatusModalProps {
  isVisible: boolean;
  onClose: (refresh?: boolean) => void;
  fundraiser: CreateFundraiserModel;
}

export const UpdateStatusModal: React.FC<UpdateStatusModalProps> = ({
  isVisible,
  onClose,
  fundraiser,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<
    keyof typeof FundRaiserStatus | null
  >();

  const allStatuses = Object.keys(FundRaiserStatus) as Array<
    keyof typeof FundRaiserStatus
  >;

  const availableStatuses = allStatuses.filter(
    (status) => status !== fundraiser.status
  );

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
    }
  };

  useEffect(() => {
    if (isVisible) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [isVisible]);

  // Reset state when modal opens
  useEffect(() => {
    if (isVisible) {
      setSelectedStatus(null);
      setIsDropdownOpen(false);
    }
  }, [isVisible]);

  const handleDropdownToggle = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleStatusSelect = (status: keyof typeof FundRaiserStatus) => {
    setSelectedStatus(status);
    setIsDropdownOpen(false);
  };

  const handleUpdateStatus = async () => {
    if (!canManageFundraiser("Update Status")) return;
    const response = await firstValueFrom(
      FundraiserService.updateStatus(
        fundraiser.id,
        AuthService.authData.accountId,
        AuthService.mindmeAccountDetails?.userId || "",
        selectedStatus || FundRaiserStatus.Active
      )
    );

    if (response.result.success) {
      ToastService.showSuccess("Fundraiser status updated successfully");
      onClose(true);
    } else {
      ToastService.showError("Status Update Failed");
    }
  };

  const getStatusMessage = (status: keyof typeof FundRaiserStatus) => {
    if (status === FundRaiserStatus.Active) {
      return "Selecting this status will allow new donations.";
    } else {
      return "Selecting this status will disable new donations.";
    }
  };

  const formatStatusDisplay = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  return createPortal(
    <ModalOverlay visible={isVisible} onClick={handleOverlayClick}>
      <ModalContent>
        <CloseButton onClick={() => onClose()}>
          <X size={20} />
        </CloseButton>

        <ModalHeader>
          <ModalTitle>Update Status</ModalTitle>
        </ModalHeader>

        <ContentContainer>
          <StatusRow>
            <StatusContainer>
              <StatusLabel>Current</StatusLabel>
              <CurrentStatus status={fundraiser.status}>
                {formatStatusDisplay(fundraiser.status)}
              </CurrentStatus>
            </StatusContainer>

            <ArrowIcon>
              <ChevronRight size={24} />
            </ArrowIcon>

            <StatusContainer>
              <StatusLabel>New</StatusLabel>
              <DropdownContainer>
                <DropdownButton
                  hasSelection={!!selectedStatus}
                  selectedStatus={selectedStatus || undefined}
                  onClick={handleDropdownToggle}
                >
                  <span>
                    {selectedStatus
                      ? formatStatusDisplay(selectedStatus)
                      : "Select Status"}
                  </span>
                  <ChevronDown
                    size={16}
                    style={{
                      transform: isDropdownOpen
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                    }}
                  />
                </DropdownButton>

                <DropdownMenu isOpen={isDropdownOpen}>
                  {availableStatuses.map((status) => (
                    <DropdownItem
                      key={status}
                      status={status}
                      onClick={() => handleStatusSelect(status)}
                    >
                      {formatStatusDisplay(status)}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </DropdownContainer>
            </StatusContainer>
          </StatusRow>

          <MessageContainer>
            {selectedStatus ? (
              <StatusMessage>{getStatusMessage(selectedStatus)}</StatusMessage>
            ) : (
              <StatusMessage style={{ color: "#9ca3af" }}>
                Select a new status to see the impact
              </StatusMessage>
            )}
          </MessageContainer>
        </ContentContainer>

        <ModalActions>
          <ModalButton onClick={() => onClose()}>Cancel</ModalButton>
          <ModalButton
            variant="primary"
            onClick={handleUpdateStatus}
            disabled={!selectedStatus}
          >
            Update Status
          </ModalButton>
        </ModalActions>
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};
